import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './MessageList.module.scss'
import MessageBubble from '../MessageBubble/MessageBubble.tsx'
import WorkOffersList from '../WorkOffersList/WorkOffersList.tsx'
import ProfessionalsList from '../ProfessionalsList/ProfessionalsList.tsx'
import MaterialQuotationsList from '../MaterialQuotationsList/MaterialQuotationsList.tsx'
import WorksToQuoteList from '../WorksToQuoteList/WorksToQuoteList.tsx'
import ServicesAndProductsList from '../ServicesAndProductsList/ServicesAndProductsList.tsx'
import MessageLimitCard from '../MessageLimitCard/MessageLimitCard.tsx'
import { Message } from '../../../../../utils/conversationStorage.ts'
import type { DesktopSuggestion } from '../../dashboardActionsConfig.tsx'
import { useSelector } from 'react-redux'
import {
  filterSuggestedActionsDedupPrimary,
  getChatbotActionUrl,
  isSuggestedActionChatFirst,
  parseSuggestedActionsFromLlmRaw,
  shouldOpenChatbotActionInNewWindow
} from '../../../../../utils/conversationalMode/chatbotSuggestedActions.ts'
import { resolveGuideSuggestionCanonicalId } from '../../../../../utils/conversationalMode/guideSuggestionActionIds.ts'
import { actionHandlers } from '../../../HomeActions/actionHandlers.ts'
import AIAgentOrbLoader from '../../../AIAgentOrb/AIAgentOrbLoader.tsx'
import orbStyles from '../../../AIAgentOrb/AIAgentOrbLoader.module.scss'
import DocumentPipelineLoopLoader from '../DocumentPipelineLoopLoader/DocumentPipelineLoopLoader.tsx'
import type { DocumentPipelineStreamHint } from '../../../../../contexts/ConversationalAgentContext.tsx'

interface MessageListProps {
  messages: Message[]
  isLoading?: boolean
  matchedSuggestion?: DesktopSuggestion | null
  onSuggestionAction?: () => void
  onRequestGuide?: () => void
  allSuggestions?: DesktopSuggestion[]
  hasReachedLimit?: boolean
  remainingMessages?: number
  limit?: number
  userData?: any
  pendingImagePreview?: string | null
  isLatestMessageGuardarPost?: boolean
  showDocumentPipelineLoop?: boolean
  documentPipelinePhase?: string | null
  documentPipelineStreamHint?: DocumentPipelineStreamHint
  documentPipelinePrimaryFileOriginalName?: string | null
  documentPipelinePrimaryFileSizeBytes?: number | null
  onSuggestedChipSend?: (label: string, actionId: string) => void
  onRequestOpenAttachmentPicker?: () => void
  onDocumentInsightContinueAnyway?: () => void
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  isLoading = false,
  onSuggestionAction,
  onRequestGuide,
  allSuggestions = [],
  hasReachedLimit = false,
  remainingMessages = 0,
  limit = 0,
  userData,
  pendingImagePreview = null,
  isLatestMessageGuardarPost = false,
  showDocumentPipelineLoop = false,
  documentPipelinePhase = null,
  documentPipelineStreamHint = null,
  documentPipelinePrimaryFileOriginalName = null,
  documentPipelinePrimaryFileSizeBytes = null,
  onSuggestedChipSend,
  onRequestOpenAttachmentPicker,
  onDocumentInsightContinueAnyway
}) => {
  const navigate = useNavigate()
  const isUserPremium = useSelector((state: any) => state.general?.isUserPremium ?? false)
  const [typingStates, setTypingStates] = useState<Record<string, boolean>>({})
  useEffect(() => {
    const messageIds = new Set(messages.map(m => m.id))
    setTypingStates(prev => {
      const cleaned: Record<string, boolean> = {}
      Object.keys(prev).forEach(id => {
        if (messageIds.has(id)) {
          cleaned[id] = prev[id]
        }
      })
      return cleaned
    })
  }, [messages])

  const handleTypingStateChange = React.useCallback((messageId: string, isTyping: boolean) => {
    setTypingStates(prev => {
      if (prev[messageId] === isTyping) {
        return prev
      }
      return {
        ...prev,
        [messageId]: isTyping
      }
    })
  }, [])

  if (messages.length === 0 && !isLoading) {
    return (
      <div className={styles.emptyState}>
        <p className={styles.emptyStateText}>
          Hacé tu primera pregunta y te ayudo a encontrar lo que necesitás.
        </p>
      </div>
    )
  }

  const lastMessage = messages[messages.length - 1]

  const getSuggestionForMessage = (message: Message): DesktopSuggestion | null => {
    const id = resolveGuideSuggestionCanonicalId(message.actionId)
    if (!id) return null
    return allSuggestions.find(s => s.id === id) || null
  }

  const renderDataCards = (message: Message) => {
    if (!message.needsData || !message.data) return null

    const { actionId, data, dataTotal = 0, quotationType, isPublic, noResultsMessage } = message

    if (actionId === 'postularme-ofertas' && data.length > 0) {
      return (
        <WorkOffersList
          offers={data}
          total={dataTotal}
          openInNewWindow={true}
        />
      )
    }

    if (actionId === 'encontrar-profesionales') {
      return (
        <ProfessionalsList
          professionals={data}
          total={dataTotal}
          noResultsMessage={noResultsMessage}
          openInNewWindow={true}
        />
      )
    }

    if (actionId === 'cotizaciones-materiales') {
      return (
        <MaterialQuotationsList
          quotations={data}
          total={dataTotal}
          quotationType={quotationType || 'privadas'}
          isPublic={isPublic || false}
          noResultsMessage={noResultsMessage}
          openInNewWindow={true}
        />
      )
    }

    if (actionId === 'cotizar-obras') {
      return (
        <WorksToQuoteList
          works={data}
          total={dataTotal}
          workVisibility={(message as any).workVisibility || 'privadas'}
          isPublic={isPublic || false}
          noResultsMessage={noResultsMessage}
          openInNewWindow={true}
        />
      )
    }

    if (actionId === 'explorar-servicios') {
      return (
        <ServicesAndProductsList
          items={data}
          total={dataTotal}
          noResultsMessage={noResultsMessage}
          openInNewWindow={true}
        />
      )
    }

    return null
  }

  const normalizeGuideSteps = (content: string): string =>
    content.replace(/(^|\n)(\d+)\s*\n/g, (_, prefix, n) => `${prefix}Paso ${n}: `)

  const isGuideMessage = (content: string): boolean => {
    const normalized = normalizeGuideSteps(content)
    return normalized.includes('Paso 1:') || normalized.includes('Paso 1 :')
  }

  const getActionUrl = (actionId: string): string | null =>
    getChatbotActionUrl(actionId, userData)

  const shouldOpenInNewWindow = (actionId: string): boolean =>
    shouldOpenChatbotActionInNewWindow(actionId)

  return (
    <div className={styles.messageList}>
      {messages.map((message) => {
        const suggestion = getSuggestionForMessage(message)
        const isLastMessage = message.id === lastMessage?.id
        const isGuide = message.role === 'assistant' && isGuideMessage(message.content)
        const isMessageTyping = typingStates[message.id] ?? false

        const suggestionForDedup = getSuggestionForMessage(message)
        const parsedSuggested = parseSuggestedActionsFromLlmRaw(message.llmRawResponse)
        const filteredSuggested = filterSuggestedActionsDedupPrimary(
          parsedSuggested,
          message,
          suggestionForDedup
        )
        const showSuggestedChips =
          message.role === 'assistant' &&
          (isLastMessage || isGuide) &&
          !isLoading &&
          !isMessageTyping &&
          filteredSuggested.length > 0 &&
          !!onSuggestedChipSend

        const shouldShowAction =
          message.role === 'assistant' &&
          suggestion &&
          resolveGuideSuggestionCanonicalId(message.actionId) === suggestion.id &&
          (isLastMessage || isGuide) &&
          !isLoading &&
          onSuggestionAction

        const shouldShowPrimaryAction = shouldShowAction && !showSuggestedChips
        const passSuggestionForGuideActions = Boolean(isGuide && shouldShowAction)

        const handleActionClick =
          (shouldShowPrimaryAction || passSuggestionForGuideActions) && suggestion
            ? () => {
                if (!suggestion) return

                if (message.actionId === 'presupuestos-obra') {
                  actionHandlers['presupuestos-obra'](navigate, userData?.id)
                  return
                }

                const actionUrl = getActionUrl(suggestion.id)
                const openInNewWindow = shouldOpenInNewWindow(suggestion.id)

                if (actionUrl && openInNewWindow) {
                  window.open(actionUrl, '_blank')
                } else if (suggestion.action) {
                  suggestion.action()
                } else if (onSuggestionAction) {
                  onSuggestionAction()
                }
              }
            : undefined

        const shouldShowDataCards = message.role === 'assistant' && !isMessageTyping && renderDataCards(message)

        const handleSuggestedChip =
          showSuggestedChips && onSuggestedChipSend
            ? (label: string, actionId: string) => {
                const dup = filteredSuggested.filter((x) => x.actionId === actionId).length > 1
                const url = getChatbotActionUrl(actionId, userData)
                const openNew = shouldOpenChatbotActionInNewWindow(actionId)
                if (dup || isSuggestedActionChatFirst(actionId)) {
                  onSuggestedChipSend(label, actionId)
                  return
                }
                if (url && openNew) {
                  window.open(url, '_blank')
                  return
                }
                if (url && !openNew) {
                  const resolvedId = resolveGuideSuggestionCanonicalId(actionId) ?? actionId
                  const sug = allSuggestions.find((s) => s.id === resolvedId)
                  if (sug?.action) {
                    sug.action()
                  } else {
                    window.location.assign(url)
                  }
                  return
                }
                onSuggestedChipSend(label, actionId)
              }
            : undefined

        return (
          <React.Fragment key={message.id}>
            <MessageBubble
              message={message}
              suggestion={
                shouldShowPrimaryAction || passSuggestionForGuideActions ? suggestion : null
              }
              onSuggestionAction={handleActionClick}
              onRequestGuide={onRequestGuide}
              isLatest={isLastMessage && !isLoading}
              onTypingStateChange={handleTypingStateChange}
              userData={userData}
              pendingImagePreview={isLastMessage && isLatestMessageGuardarPost ? pendingImagePreview : null}
              suggestedActions={showSuggestedChips ? filteredSuggested : undefined}
              onSuggestedChip={handleSuggestedChip}
              onRequestOpenAttachmentPicker={onRequestOpenAttachmentPicker}
              onDocumentInsightContinueAnyway={onDocumentInsightContinueAnyway}
            />
            {shouldShowDataCards}
          </React.Fragment>
        )
      })}
      {hasReachedLimit && !isUserPremium && messages.length > 0 && (
        <MessageLimitCard
          userData={userData}
          isUserPremium={isUserPremium}
          remainingMessages={remainingMessages}
          limit={limit}
        />
      )}
      {isLoading &&
        (showDocumentPipelineLoop ? (
          <DocumentPipelineLoopLoader
            pipelinePhase={documentPipelinePhase}
            streamHint={documentPipelineStreamHint}
            primaryFileOriginalName={documentPipelinePrimaryFileOriginalName}
            primaryFileSizeBytes={documentPipelinePrimaryFileSizeBytes}
          />
        ) : (
          <div className={styles.loadingIndicator}>
            <AIAgentOrbLoader status="thinking" className={orbStyles.thinkingOrb} />
            <span>Pensando...</span>
          </div>
        ))}
    </div>
  )
}

export default MessageList
